/**
 * Delivery date (#1219) — storefront mirror of the b2b-api
 * `dealer-inventory/utils/delivery-date.util.ts` contract.
 *
 * The API normalises the Sheet cell before it ships, so the widget only ever
 * receives one of:
 *   'immediate'  — in stock, deliverable now
 *   'YYYY-MM'    — expected in that calendar month
 *   '' / null    — the dealer hasn't stated one
 *
 * We still guard the shape here: `formatDeliveryDate` returns '' for anything
 * unrecognised so a stray value can never render as a half-parsed promise on a
 * customer-facing page. Callers hide the row on a falsy result.
 */

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** The canonical "in stock now" sentinel. */
export const DELIVERY_IMMEDIATE = 'immediate';

/**
 * The canonical cell as the agent receives it with a car row (#1488): trimmed, or
 * null when the dealer stated none. The agent renders it as the car's arrival in its
 * own words, on its own clock, so the widget passes the data and no wording.
 *
 * @param {unknown} value - `car.deliveryDate`
 * @returns {string|null}
 */
export function deliveryCell(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Buyer-facing delivery text for the VDP pill, or '' when we shouldn't state one.
 *
 * Phrasing is deliberately conversational rather than a bare date — a buyer reads
 * "Arriving in October", not "October 2026". The YEAR is omitted when the month
 * falls in the current calendar year (it's noise: nobody says "arriving in
 * October 2026" in July 2026) and kept when it doesn't, so a January delivery
 * from December can't be mistaken for next month.
 *
 * Returns '' not only for an unset/unreadable value but also for a month that has
 * already gone by (#1219): a dealer sets "August 2026", forgets it, and by
 * September the page would be advertising a date that is visibly in the past. We
 * don't upgrade it to "Available immediately" either — an elapsed month means the
 * plan slipped, not that the car landed. Saying nothing is the honest option;
 * b2b-ui shows the dealer the stale value, flagged, so they can correct it.
 *
 * @param {{ deliveryDate?: string|null }} [car]
 * @param {Date} [now] injectable for tests
 * @returns {string} 'Available now' | 'Arriving in October' | 'Arriving in January 2027' | ''
 */
export function formatDeliveryDate(car, now = new Date()) {
  const raw = typeof car?.deliveryDate === 'string' ? car.deliveryDate.trim() : '';
  if (!raw) return '';
  if (raw === DELIVERY_IMMEDIATE) return 'Available now';
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(raw);
  if (!match) return '';
  // Zero-padded YYYY-MM, so a plain string compare is a chronological compare.
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  if (raw < thisMonth) return '';
  const year = Number(match[1]);
  const monthName = MONTH_NAMES[Number(match[2]) - 1];
  return year === now.getFullYear()
    ? `Arriving in ${monthName}`
    : `Arriving in ${monthName} ${year}`;
}
